/**
 * canvas-engine/presets/presetData/genericPresets.js
 *
 * TRANSCRIBED generic / normal (cross-domain) section default VALUES.
 *
 * ZERO-REUSE CONSTRAINT (Task 3.1): imports NOTHING. These cover the shared,
 * non-family "normal" section types listed in the Default_Source
 * (`educationSectionTypes` generic entries) and the generic fallback section
 * (the default branch of `createEducationSection`). Values transcribed as plain
 * DATA. No React, no rendering, no side effects.
 *
 * The `generic` descriptor is also the fallback the decomposition adapters use
 * when no family-specific descriptor exists (Task 3.2 buildSectionCanvas, 3.3
 * generic.js, and Task 4 migration fallback).
 *
 * See educationPresets.js for the shared descriptor shape.
 *
 * Requirements: 3.6, 3.7, 5.1, 5.6, 5.7
 */

// ── Local pure data helpers ──
const action = (label, url, variant = 'primary') => ({ label, url, variant });
const block = (title, description = '', accentColor = '#3B9394', icon = '') => ({
  title,
  description,
  accentColor,
  icon,
});
const style = (overrides = {}) => ({
  background: '#ffffff',
  textColor: '#111827',
  accentColor: '#3B9394',
  padding: '56px 24px',
  ...overrides,
});

/**
 * GENERIC_PRESETS — per-section-type default descriptors for the shared/normal
 * Section_Family. These types fall through to the generic look in the
 * Default_Source, so the transcribed defaults below reproduce that quiet,
 * neutral look (white background, accent #3B9394) while remaining fully
 * editable Canvas content.
 */
export const GENERIC_PRESETS = {
  // The universal fallback descriptor (matches the Default_Source generic branch).
  generic: {
    sectionType: 'generic',
    label: 'Section',
    domain: 'generic',
    style: style({ minHeight: '320px' }),
    layout: { align: 'left', width: 'contained' },
    content: {
      eyebrow: 'Featured',
      heading: 'Build a polished storefront',
      subheading: 'Customize this section from the editor.',
      actions: [action('Explore', '/', 'primary')],
    },
  },

  hero: {
    sectionType: 'hero',
    label: 'Hero banner',
    domain: 'generic',
    style: style({ background: '#111827', textColor: '#ffffff', padding: '80px 32px', minHeight: '420px' }),
    layout: { align: 'left', width: 'contained', columns: 2 },
    content: {
      eyebrow: 'Welcome',
      heading: 'A headline that introduces your brand',
      subheading: 'Add a short supporting sentence, media, and calls to action.',
      actions: [
        action('Get started', '/', 'primary'),
        action('Learn more', '/', 'outline'),
      ],
    },
  },

  imageBanner: {
    sectionType: 'imageBanner',
    label: 'Image banner',
    domain: 'generic',
    style: style({ background: '#0f172a', textColor: '#ffffff', padding: '96px 32px', minHeight: '360px' }),
    layout: { align: 'center', width: 'full' },
    content: {
      heading: 'Full-width visual statement',
      subheading: 'Overlay text on a large background image.',
      images: [{ storedLink: '', displayLink: '' }],
      actions: [action('Explore', '/', 'primary')],
    },
  },

  richText: {
    sectionType: 'richText',
    label: 'Rich text',
    domain: 'generic',
    style: style({ background: '#ffffff', textColor: '#1f2937', padding: '56px 24px' }),
    layout: { align: 'left', width: 'contained' },
    content: {
      heading: 'Section title',
      body:
        '<p>Use this editorial block for policy copy, page introductions, or long-form content. Edit the HTML safely from the inspector.</p>',
    },
  },

  featureGrid: {
    sectionType: 'featureGrid',
    label: 'Feature grid',
    domain: 'generic',
    style: style({ background: '#ffffff', textColor: '#1f2937', padding: '64px 24px' }),
    layout: { align: 'center', width: 'contained', columns: 3 },
    content: {
      heading: 'What we offer',
      subheading: 'Highlight benefits, services, or features in editable cards.',
      cards: [
        block('Benefit one', 'Describe a key benefit or capability.', '#3B9394', 'Star'),
        block('Benefit two', 'Describe a key benefit or capability.', '#87927c', 'Check'),
        block('Benefit three', 'Describe a key benefit or capability.', '#3B9394', 'Bolt'),
      ],
    },
  },

  productRail: {
    sectionType: 'productRail',
    label: 'Product rail',
    domain: 'generic',
    style: style({ background: '#f8fafc', textColor: '#1f2937', padding: '64px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 4 },
    content: {
      heading: 'Featured items',
      subheading: 'A bound product, course, or collection preview.',
      emptyStateTitle: 'Items will appear here once published.',
      cards: [],
      // Bound rail — live items render through a dataBound element.
      binding: { source: 'products', limit: 4 },
    },
  },

  faq: {
    sectionType: 'faq',
    label: 'FAQ',
    domain: 'generic',
    style: style({ background: '#ffffff', textColor: '#1f2937', padding: '64px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 1 },
    content: {
      heading: 'Frequently asked questions',
      subheading: 'Answer common questions in an accordion.',
      items: [
        block('Question one?', 'Answer to the first common question.'),
        block('Question two?', 'Answer to the second common question.'),
        block('Question three?', 'Answer to the third common question.'),
      ],
    },
  },

  testimonials: {
    sectionType: 'testimonials',
    label: 'Testimonials',
    domain: 'generic',
    style: style({ background: '#f8fafc', textColor: '#1f2937', padding: '64px 24px' }),
    layout: { align: 'center', width: 'contained', columns: 3 },
    content: {
      heading: 'What people say',
      subheading: 'Quotes, reviews, and social proof.',
      cards: [
        { ...block('Customer name', 'Role / company', '#3B9394'), quote: 'A short, positive quote about the experience.' },
        { ...block('Customer name', 'Role / company', '#87927c'), quote: 'A short, positive quote about the experience.' },
        { ...block('Customer name', 'Role / company', '#3B9394'), quote: 'A short, positive quote about the experience.' },
      ],
    },
  },

  contactBlock: {
    sectionType: 'contactBlock',
    label: 'Contact block',
    domain: 'generic',
    style: style({ background: '#ffffff', textColor: '#1f2937', padding: '64px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 2 },
    content: {
      heading: 'Get in touch',
      subheading: 'Email, phone, address, and support content.',
      phoneTitle: 'Phone',
      phoneValue: '+1 000-000-0000',
      emailTitle: 'Email',
      emailValue: 'hello@example.com',
      addressTitle: 'Address',
      addressValue: 'Your address',
    },
  },

  cartSummary: {
    sectionType: 'cartSummary',
    label: 'Cart or summary wrapper',
    domain: 'generic',
    style: style({ background: '#ffffff', textColor: '#1f2937', padding: '0' }),
    layout: { align: 'left', width: 'full' },
    content: {
      heading: 'Cart',
      subheading: 'Keeps checkout/cart logic inside the template.',
      binding: { source: 'cart' },
    },
  },
};

/** Generic / normal section types (transcribed from `educationSectionTypes`). */
export const genericSectionPresets = [
  { type: 'hero', title: 'Hero banner', description: 'Generic headline, media, and calls to action.' },
  { type: 'imageBanner', title: 'Image banner', description: 'Full-width visual section with overlay text.' },
  { type: 'richText', title: 'Rich text', description: 'Editorial content, policy copy, or page intro.' },
  { type: 'featureGrid', title: 'Feature grid', description: 'Cards for benefits, services, or highlights.' },
  { type: 'productRail', title: 'Product rail', description: 'Bound product/course/collection preview.' },
  { type: 'faq', title: 'FAQ', description: 'Accordion questions and answers.' },
  { type: 'testimonials', title: 'Testimonials', description: 'Quotes, reviews, and social proof.' },
  { type: 'contactBlock', title: 'Contact block', description: 'Email, phone, address, and support content.' },
  { type: 'cartSummary', title: 'Cart or summary wrapper', description: 'Keeps checkout/cart logic inside the template.' },
];

export default GENERIC_PRESETS;
